import React, { useState, useEffect } from 'react';
import NavigationBar from '../NavigationBar';
import axios from '../axios';
import { Container, Row, Col, Table, Button, Form, Modal, Nav, Badge } from 'react-bootstrap';

const VENDOR_TYPES = ['Supplier', 'CF Agent', 'Transporter', 'Insurance'];

const emptyVendor = {
    vendorType: 'Supplier', supplyCategory: '', name: '', country: 'Bangladesh', isForeign: false,
    address: '', contactPerson: '', email: '', whatsApp: '', phone: '',
    agentName: '', agentContact: '', agentEmail: '', agentWhatsApp: '',
    currency: 'BDT', defaultIncoterm: '', defaultPortOfLoading: '', defaultPaymentTerm: '',
    leadTimeDays: '', bankName: '', bankAddress: '', bankAccountNo: '', bankSwift: '',
    notes: '', isActive: true,
};

function Vendors() {
    const [activeType, setActiveType] = useState('Supplier');
    const [vendors, setVendors] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(emptyVendor);
    const [editingId, setEditingId] = useState(null);

    useEffect(() => { fetchVendors(); }, [activeType]);

    const fetchVendors = async () => {
        try {
            const res = await axios.get(`${window.backendUrl}/purchase_vendors`, { params: { type: activeType } });
            setVendors(res.data.data || []);
        } catch (err) {
            console.error('Error fetching vendors:', err);
        }
    };

    const openAdd = () => {
        setForm({ ...emptyVendor, vendorType: activeType });
        setEditingId(null);
        setShowModal(true);
    };

    const openEdit = (v) => {
        setForm({
            vendorType: v.VendorType, supplyCategory: v.SupplyCategory || '', name: v.Name,
            country: v.Country || 'Bangladesh', isForeign: !!v.IsForeign, address: v.Address || '',
            contactPerson: v.ContactPerson || '', email: v.Email || '', whatsApp: v.WhatsApp || '', phone: v.Phone || '',
            agentName: v.AgentName || '', agentContact: v.AgentContact || '', agentEmail: v.AgentEmail || '',
            agentWhatsApp: v.AgentWhatsApp || '', currency: v.Currency || 'BDT',
            defaultIncoterm: v.DefaultIncoterm || '', defaultPortOfLoading: v.DefaultPortOfLoading || '',
            defaultPaymentTerm: v.DefaultPaymentTerm || '', leadTimeDays: v.LeadTimeDays ?? '',
            bankName: v.BankName || '', bankAddress: v.BankAddress || '', bankAccountNo: v.BankAccountNo || '',
            bankSwift: v.BankSwift || '', notes: v.Notes || '', isActive: !!v.IsActive,
        });
        setEditingId(v.VendorID);
        setShowModal(true);
    };

    const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        if (!form.name) { alert('Name is required'); return; }
        try {
            if (editingId) {
                await axios.put(`${window.backendUrl}/purchase_vendors/${editingId}`, form);
            } else {
                await axios.post(`${window.backendUrl}/purchase_vendors`, form);
            }
            setShowModal(false);
            fetchVendors();
        } catch (err) {
            console.error('Error saving vendor:', err);
            alert('Failed to save vendor.');
        }
    };

    return (
        <>
            <NavigationBar />
            <Container fluid style={{ marginTop: 20 }}>
                <Row className="align-items-center mb-3">
                    <Col xs="auto"><h3>Vendors</h3></Col>
                    <Col className="text-end">
                        <Button variant="primary" onClick={openAdd}>+ Add {activeType}</Button>
                    </Col>
                </Row>
                <Nav variant="tabs" activeKey={activeType} onSelect={(k) => setActiveType(k)} className="mb-3">
                    {VENDOR_TYPES.map(t => <Nav.Item key={t}><Nav.Link eventKey={t}>{t}{t === 'Supplier' ? 's' : t === 'CF Agent' ? 's' : 's'}</Nav.Link></Nav.Item>)}
                </Nav>
                <Table striped bordered hover responsive size="sm">
                    <thead>
                        <tr>
                            <th>Name</th>
                            {activeType === 'Supplier' && <th>Category</th>}
                            <th>Country</th>
                            {activeType === 'Supplier' && <th>Foreign?</th>}
                            <th>Contact</th>
                            <th>Phone / WhatsApp</th>
                            {activeType === 'Supplier' && <th>Lead time (days)</th>}
                            <th>Active</th>
                            <th>Edit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vendors.length === 0 ? (
                            <tr><td colSpan={8} className="text-center text-muted" style={{ padding: 20 }}>No {activeType.toLowerCase()}s yet.</td></tr>
                        ) : vendors.map(v => (
                            <tr key={v.VendorID}>
                                <td>{v.Name}</td>
                                {activeType === 'Supplier' && <td>{v.SupplyCategory || '-'}</td>}
                                <td>{v.Country}</td>
                                {activeType === 'Supplier' && <td>{v.IsForeign ? <Badge bg="info">Import</Badge> : <Badge bg="secondary">Local</Badge>}</td>}
                                <td>{v.ContactPerson || '-'}</td>
                                <td>{v.Phone || v.WhatsApp || '-'}</td>
                                {activeType === 'Supplier' && <td>{v.LeadTimeDays ?? '-'}</td>}
                                <td>{v.IsActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}</td>
                                <td><Button size="sm" variant="outline-primary" onClick={() => openEdit(v)}>Edit</Button></td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Container>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton><Modal.Title>{editingId ? 'Edit' : 'Add'} {form.vendorType}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Row className="mb-2">
                        <Col md={4}>
                            <Form.Label>Type</Form.Label>
                            <Form.Select value={form.vendorType} onChange={e => handleChange('vendorType', e.target.value)}>
                                {VENDOR_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </Form.Select>
                        </Col>
                        {form.vendorType === 'Supplier' && (
                            <Col md={4}>
                                <Form.Label>Supplies</Form.Label>
                                <Form.Select value={form.supplyCategory} onChange={e => handleChange('supplyCategory', e.target.value)}>
                                    <option value="">Select</option>
                                    <option value="Direct">Direct (paper)</option>
                                    <option value="Indirect">Indirect (other materials)</option>
                                    <option value="Both">Both</option>
                                </Form.Select>
                            </Col>
                        )}
                        {form.vendorType === 'Supplier' && (
                            <Col md={4} className="d-flex align-items-end">
                                <Form.Check type="checkbox" label="Foreign supplier (import / LC)" checked={form.isForeign} onChange={e => handleChange('isForeign', e.target.checked)} />
                            </Col>
                        )}
                    </Row>
                    <Row className="mb-2">
                        <Col md={6}>
                            <Form.Label>Name</Form.Label>
                            <Form.Control value={form.name} onChange={e => handleChange('name', e.target.value)} />
                        </Col>
                        <Col md={6}>
                            <Form.Label>Country</Form.Label>
                            <Form.Control value={form.country} onChange={e => handleChange('country', e.target.value)} />
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}>
                            <Form.Label>Address</Form.Label>
                            <Form.Control value={form.address} onChange={e => handleChange('address', e.target.value)} />
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={3}><Form.Label>Contact person</Form.Label><Form.Control value={form.contactPerson} onChange={e => handleChange('contactPerson', e.target.value)} /></Col>
                        <Col md={3}><Form.Label>Email</Form.Label><Form.Control value={form.email} onChange={e => handleChange('email', e.target.value)} /></Col>
                        <Col md={3}><Form.Label>Phone</Form.Label><Form.Control value={form.phone} onChange={e => handleChange('phone', e.target.value)} /></Col>
                        <Col md={3}><Form.Label>WhatsApp</Form.Label><Form.Control value={form.whatsApp} onChange={e => handleChange('whatsApp', e.target.value)} /></Col>
                    </Row>
                    {form.isForeign && (
                        <>
                            <hr />
                            <p className="text-muted mb-1">Local agent (if any)</p>
                            <Row className="mb-2">
                                <Col md={3}><Form.Label>Agent name</Form.Label><Form.Control value={form.agentName} onChange={e => handleChange('agentName', e.target.value)} /></Col>
                                <Col md={3}><Form.Label>Agent contact</Form.Label><Form.Control value={form.agentContact} onChange={e => handleChange('agentContact', e.target.value)} /></Col>
                                <Col md={3}><Form.Label>Agent email</Form.Label><Form.Control value={form.agentEmail} onChange={e => handleChange('agentEmail', e.target.value)} /></Col>
                                <Col md={3}><Form.Label>Agent WhatsApp</Form.Label><Form.Control value={form.agentWhatsApp} onChange={e => handleChange('agentWhatsApp', e.target.value)} /></Col>
                            </Row>
                        </>
                    )}
                    <hr />
                    <Row className="mb-2">
                        <Col md={3}><Form.Label>Currency</Form.Label><Form.Control value={form.currency} onChange={e => handleChange('currency', e.target.value)} /></Col>
                        <Col md={3}><Form.Label>Incoterm</Form.Label><Form.Control value={form.defaultIncoterm} onChange={e => handleChange('defaultIncoterm', e.target.value)} placeholder="CFR, FOB..." /></Col>
                        <Col md={3}><Form.Label>Port of loading</Form.Label><Form.Control value={form.defaultPortOfLoading} onChange={e => handleChange('defaultPortOfLoading', e.target.value)} /></Col>
                        <Col md={3}>
                            <Form.Label>Lead time (days)</Form.Label>
                            <Form.Control type="number" min="0" value={form.leadTimeDays} onChange={e => handleChange('leadTimeDays', e.target.value)} placeholder="e.g. 45" />
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}><Form.Label>Payment term</Form.Label><Form.Control value={form.defaultPaymentTerm} onChange={e => handleChange('defaultPaymentTerm', e.target.value)} placeholder="e.g. 15% margin, EDF LC at sight" /></Col>
                    </Row>
                    <hr />
                    <p className="text-muted mb-1">Bank details</p>
                    <Row className="mb-2">
                        <Col md={6}><Form.Label>Bank name</Form.Label><Form.Control value={form.bankName} onChange={e => handleChange('bankName', e.target.value)} /></Col>
                        <Col md={6}><Form.Label>Bank address</Form.Label><Form.Control value={form.bankAddress} onChange={e => handleChange('bankAddress', e.target.value)} /></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={6}><Form.Label>Account no.</Form.Label><Form.Control value={form.bankAccountNo} onChange={e => handleChange('bankAccountNo', e.target.value)} /></Col>
                        <Col md={6}><Form.Label>SWIFT</Form.Label><Form.Control value={form.bankSwift} onChange={e => handleChange('bankSwift', e.target.value)} /></Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={12}><Form.Label>Notes</Form.Label><Form.Control as="textarea" rows={2} value={form.notes} onChange={e => handleChange('notes', e.target.value)} /></Col>
                    </Row>
                    <Form.Check type="checkbox" label="Active" checked={form.isActive} onChange={e => handleChange('isActive', e.target.checked)} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave}>Save</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default Vendors;
